import React, { createContext, useContext } from 'react';

const defaultConfig = Object.freeze({
  appName: 'SONDA',
  api: {
    base: '',
    healthcheck: '',
    auth: { version: '', baseUrl: '' },
    test: { version: '', baseUrl: '' },
    report: { version: '', baseUrl: '' },
    acquire: { version: '', baseUrl: '' },
  },
  urls: {
    manual: '',
    source: '',
  },
  ws: {
    stream: '',
    streamEye: '',
    jumps: '',
  },
  features: {
    bypassCompatibilityDefault: false,
    isLocalHost: false,
    showVersionBanner: false,
    phoneRequiredDefault: true,
  },
  cognito: {
    userPoolId: '',
    clientId: '',
    region: '',
    identityPoolId: '',
  },
  compatibilityStorage: {
    bucketName: '',
    fileKey: '',
  },
});

const AppConfigContext = createContext(null);

const resolveHostname = () => {
  if (typeof window === 'undefined' || !window.location) return '';
  return window.location.hostname;
};

const buildServiceConfig = (serverUri, version) => {
  const normalizedVersion = version ?? '';
  if (!serverUri || !normalizedVersion) {
    return { version: normalizedVersion, baseUrl: '' };
  }
  return {
    version: normalizedVersion,
    baseUrl: `${serverUri}/${normalizedVersion}`,
  };
};

const createAppConfig = () => {
  const hostname = resolveHostname();
  const hostIsLocal = ['localhost', '127.0.0.1'].includes(hostname);
  const env = import.meta.env ?? {};
  const serverUri = env.VITE_APP_SERVER_URI ?? '';

  return Object.freeze({
    appName: env.VITE_APP_NAME ?? defaultConfig.appName,

    api: {
      base: serverUri,
      healthcheck: env.VITE_APP_SERVER_HEALTHCHECK_URI ?? '',
      auth: buildServiceConfig(serverUri, env.VITE_APP_AUTH_SERVICE_API_VERSION),
      test: buildServiceConfig(serverUri, env.VITE_APP_TEST_SERVICE_API_VERSION),
      report: buildServiceConfig(serverUri, env.VITE_APP_REPORT_SERVICE_API_VERSION),
      acquire: buildServiceConfig(serverUri, env.VITE_APP_DATA_ACQUISITION_API_VERSION),
    },

    urls: {
      manual: env.VITE_APP_MANUAL_URL ?? '',
      source: env.VITE_APP_SOURCE_URL ?? '',
    },

    ws: {
      stream: env.VITE_APP_SERVER_STREAM_WS_URI ?? '',
      streamEye: env.VITE_APP_SERVER_STREAM_WS_EYE_URI ?? '',
      jumps: env.VITE_APP_SERVER_STREAM_JUMPS_WS_URI ?? '',
    },
    features: {
      bypassCompatibilityDefault: hostIsLocal,
      isLocalHost: hostIsLocal,
      showVersionBanner: env.VITE_FEATURE_SHOW_VERSION === 'true',
      phoneRequiredDefault: env.VITE_FEATURE_PHONE_REQUIRED_DEFAULT !== 'false',
    },
    cognito: {
      userPoolId: env.VITE_APP_USER_POOL_ID ?? '',
      clientId: env.VITE_APP_CLIENT_ID ?? '',
      region: env.VITE_APP_REGION ?? '',
      identityPoolId: env.VITE_APP_IDENTITY_POOL_ID ?? '',
    },
    compatibilityStorage: {
      bucketName: env.VITE_APP_BUCKET_NAME ?? '',
      fileKey: env.VITE_APP_FILE_KEY ?? '',
    },
  });
};

const configCache = createAppConfig();

export const AppConfigProvider = ({ children, value }) => (
  <AppConfigContext.Provider value={value ?? configCache}>{children}</AppConfigContext.Provider>
);

export const useAppConfig = () => {
  const context = useContext(AppConfigContext);
  if (!context) {
    throw new Error('useAppConfig must be used inside AppConfigProvider');
  }
  return context;
};

export const getAppConfig = () => configCache;
